import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { NavigationContext } from '../../NavigationContext';
import { TabsPage } from '../../TabsPage';
import { isTabRouteObject } from '../types';
import { createLink, mapRoutesToItemsAndFindSelected, navigateToCallback } from './RouteUtils';
/**
 * Renders a tabbed page layout using route configuration metadata. It determines the
 * active tab from the route and redirects to the first tab if none is selected.
 *
 * @param {TabsPageRouteElementProps} props - Component props.
 * @param {Omit<RouteObject, 'handle'> & { handle: TabsRouteHandle }} props.route - The current route object containing tab metadata.
 * @param {string} props.basename - The base path used to construct route links.
 *
 * @returns {JSX.Element} The rendered tabbed page with navigation and an `<Outlet />` for nested routes.
 */
export const TabsPageRouteElement = (props) => {
    var _a, _b;
    const location = useLocation();
    const navigate = useNavigate();
    const navigationContext = React.useContext(NavigationContext);
    const parentPath = createLink([props.basename, (_a = props.route.path) !== null && _a !== void 0 ? _a : '']);
    const [tabs, selectedTabId] = mapRoutesToItemsAndFindSelected(createLink([props.basename, location.pathname]), (_b = props.route.children) !== null && _b !== void 0 ? _b : [], (route) => routeToTab(parentPath, navigate, navigationContext, route));
    React.useEffect(() => {
        if (selectedTabId == null && tabs.length > 0) {
            navigate(tabs[0].navigateTo, { replace: true });
        }
    }, [navigate, tabs, selectedTabId]);
    return (React.createElement(TabsPage, { gradient: props.route.handle.gradient, title: props.route.handle.label, tabs: tabs, header: props.route.handle.header, selectedTabId: selectedTabId, fullHeightChildren: props.route.handle.fullHeightChildren },
        React.createElement(Outlet, null)));
};
function routeToTab(parentPath, navigate, navigationContext, route) {
    var _a, _b, _c;
    if ((route.path || route.index) && isTabRouteObject(route)) {
        const path = (_a = route.path) !== null && _a !== void 0 ? _a : '';
        const handle = route.handle;
        const navigateTo = createLink([(_b = handle.navigateTo) !== null && _b !== void 0 ? _b : path]);
        return {
            id: path,
            label: handle.label,
            badge: handle.badge,
            base: createLink([parentPath, path]),
            href: createLink([parentPath, (_c = handle.navigateTo) !== null && _c !== void 0 ? _c : path]),
            fullHeightScrollable: handle.fullHeightScrollable,
            onClick: navigateToCallback(navigationContext, navigate, navigateTo, handle.onClick),
            'data-test': handle['data-test'],
            navigateTo,
        };
    }
    else {
        return undefined;
    }
}
